import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { State } from 'src/app/store';
import { environmentSelector } from 'src/app/store/global/global.selectors';

export interface UploadImageDto {
    fileName: string;
    fileContent: string; // Base64 encoded file content
}

@Injectable({
    providedIn: 'root',
})
export class AzureStorageService {
    private apiUrl: string;

    constructor(private http: HttpClient, private store: Store<State>) {
        this.store.select(environmentSelector).subscribe((env) => {
            if (env && env.baseUrl) {
                this.apiUrl = `${env.baseUrl}/market/admin/vendor`;
            }
        });
    }

    uploadShopPicture(fileName: string, fileContent: string): Observable<{ url: string }> {
        const uploadData: UploadImageDto = {
            fileName,
            fileContent,
        };

        return this.http.post(this.apiUrl + '/picture', uploadData).pipe(
            map((response: any) => {
                return { url: response.url };
            }),
        );
    }

    deleteShopIcon(fileName: string): Observable<any> {
        return this.http.delete(`${this.apiUrl}/picture?fileName=${encodeURIComponent(fileName)}`);
    }

    uploadShopIcon(fileName: string, fileContent: string): Observable<{ url: string }> {
        const uploadData: UploadImageDto = {
            fileName,
            fileContent,
        };

        return this.http.post(this.apiUrl + '/icon', uploadData).pipe(
            map((response: any) => {
                return { url: response.url };
            }),
        );
    }

    deleteShopPicture(fileName: string): Observable<any> {
        return this.http.delete(`${this.apiUrl}/icon?fileName=${encodeURIComponent(fileName)}`);
    }

    convertFileToBase64(file: File): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();

            reader.onload = () => {
                try {
                    // FileReader result contains the data URL with format: data:[mimetype];base64,[base64data]
                    const base64String = reader.result as string;

                    // Validate the result format
                    if (!base64String || base64String.indexOf(',') === -1) {
                        reject(new Error('Invalid file format or empty file'));
                        return;
                    }

                    // Extract only the base64 data portion after the comma
                    const base64Content = base64String.split(',')[1];
                    resolve(base64Content);
                } catch (error) {
                    reject(new Error(`Failed to process file: ${error instanceof Error ? error.message : String(error)}`));
                }
            };

            reader.onerror = () => {
                reject(new Error(`File reading failed: ${reader.error?.message || 'Unknown error'}`));
            };

            // Start reading the file as a data URL
            reader.readAsDataURL(file);
        });
    }
}
